import React, { useEffect } from 'react';
import { Form, Row, Col } from 'react-bootstrap';
import { useForm, Controller } from 'react-hook-form';
import { useToast } from 'providers/ToastProvider';
import Button from 'components/base/Button';
import { useEmpresas } from 'providers/EmpresaProvider';
import { useNFe, useAtualizarCabecalho } from '../../../../hooks/useNFe';
import { AtualizarCabecalhoRequest } from '../../../../types/nfe';
import { toDateTimeInputValue, fromDateTimeInputValue } from '../../../../utils/dateUtils';
import NaturezaOperacaoInput from '../../components/NaturezaOperacaoInput';

interface SecaoCabecalhoProps {
  nfeId: string;
  isReadOnly: boolean;
  onDirtyChange?: (isDirty: boolean) => void;
}

const SecaoCabecalho: React.FC<SecaoCabecalhoProps> = ({ nfeId, isReadOnly, onDirtyChange }) => {
  const { showToast } = useToast();
  const { empresas } = useEmpresas();
  const { data: nfe } = useNFe(nfeId);
  const mutation = useAtualizarCabecalho(nfeId);

  const defaultValues: AtualizarCabecalhoRequest & { empresaId: string } = {
    serie: nfe?.serie ?? 1,
    numero: nfe?.numero ?? 1,
    naturezaOperacao: nfe?.naturezaOperacao ?? '',
    finalidadeNFe: nfe?.finalidadeNFe ?? '1',
    tipoNota: nfe?.tipoNota ?? '1',
    indicadorDestinatario: nfe?.indicadorDestinatario ?? '',
    indicadorFinal: nfe?.indicadorFinal ?? '',
    indicadorPresenca: nfe?.indicadorPresenca ?? '',
    empresaId: nfe?.empresaId ?? '',
    dhSaiEnt: toDateTimeInputValue(nfe?.dataSaidaEntrada),
  };

  const {
    register,
    handleSubmit,
    reset,
    watch,
    control,
    formState: { isDirty },
  } = useForm({ defaultValues });

  const watchedEmpresaId = watch('empresaId');
  const selectedEmpresa = empresas.find(e => e.id === watchedEmpresaId);

  useEffect(() => {
    onDirtyChange?.(isDirty);
  }, [isDirty, onDirtyChange]);

  useEffect(() => {
    if (!nfe) return;
    reset({
      serie: nfe.serie ?? 1,
      numero: nfe.numero ?? 1,
      naturezaOperacao: nfe.naturezaOperacao ?? '',
      finalidadeNFe: nfe.finalidadeNFe ?? '1',
      tipoNota: nfe.tipoNota ?? '1',
      indicadorDestinatario: nfe.indicadorDestinatario ?? '',
      indicadorFinal: nfe.indicadorFinal ?? '',
      indicadorPresenca: nfe.indicadorPresenca ?? '',
      empresaId: nfe.empresaId ?? '',
      dhSaiEnt: toDateTimeInputValue(nfe.dataSaidaEntrada),
    });
  }, [nfe, reset]);

  const onSubmit = async (data: typeof defaultValues) => {
    try {
      const request: AtualizarCabecalhoRequest = {
        serie: Number(data.serie),
        numero: Number(data.numero),
        naturezaOperacao: data.naturezaOperacao,
        finalidadeNFe: data.finalidadeNFe,
        tipoNota: data.tipoNota,
        indicadorDestinatario: data.indicadorDestinatario || undefined,
        indicadorFinal: data.indicadorFinal || undefined,
        indicadorPresenca: data.indicadorPresenca || undefined,
        empresaId: data.empresaId !== nfe?.empresaId ? data.empresaId : undefined,
        dhSaiEnt: data.dhSaiEnt ? fromDateTimeInputValue(data.dhSaiEnt) : undefined,
      };
      await mutation.mutateAsync(request);
      showToast('Identificação atualizada com sucesso', 'success');
      reset(data);
    } catch (error: any) {
      const msg = error?.response?.data?.message ?? 'Erro ao salvar identificação';
      showToast(msg, 'error');
    }
  };

  const crt = nfe?.emitente?.regimeTributario;
  const regimeLabel =
    crt === '1' || crt === '0'
      ? 'Simples Nacional'
      : crt === '2'
      ? 'Presumido'
      : 'Regime Normal';

  return (
    <Form onSubmit={handleSubmit(onSubmit)}>
      {/* ── Emitente ─────────────────────────────── */}
      <div className="nfe-dest-section">
        <div className="nfe-dest-section-header">
          <span className="nfe-dest-section-title">Emitente</span>
        </div>
        <Row className="g-3">
          <Col xs={12} md={6}>
            <label className="nfe-dest-label">Empresa</label>
            {isReadOnly ? (
              <div className="small fw-medium">
                {nfe?.emitente?.nomeRazaoSocial}
                {nfe?.emitente?.nomeFantasia && nfe.emitente!.nomeFantasia !== nfe.emitente!.nomeRazaoSocial && (
                  <span className="text-body-secondary ms-1">({nfe.emitente!.nomeFantasia})</span>
                )}
              </div>
            ) : (
              <Form.Select size="sm" {...register('empresaId')}>
                {empresas.map(e => (
                  <option key={e.id} value={e.id}>
                    {e.nomeFantasia}
                  </option>
                ))}
              </Form.Select>
            )}
          </Col>
          <Col xs={6} md={3}>
            <label className="nfe-dest-label">CNPJ</label>
            <div className="small fw-medium">{selectedEmpresa?.cnpj ?? nfe?.emitente?.cnpj ?? '—'}</div>
          </Col>
          <Col xs={6} md={3}>
            <label className="nfe-dest-label">Regime</label>
            <div className="small fw-medium">{regimeLabel}</div>
          </Col>
        </Row>
      </div>

      {/* ── Identificação ────────────────────────── */}
      <div className="nfe-dest-section">
        <div className="nfe-dest-section-header">
          <span className="nfe-dest-section-title">Identificação</span>
        </div>
        <Row className="g-3">
          <Col xs={6} md={2}>
            <label className="nfe-dest-label">Modelo</label>
            <div className="small fw-medium">{nfe?.modelo === '65' ? '65 — NFC-e' : '55 — NF-e'}</div>
          </Col>
          <Col xs={6} md={2}>
            <Form.Group>
              <label className="nfe-dest-label">Série *</label>
              <Form.Control
                size="sm"
                type="number"
                {...register('serie', { required: true, min: 1, max: 999 })}
                disabled={isReadOnly}
                min={1}
                max={999}
              />
            </Form.Group>
          </Col>
          <Col xs={6} md={2}>
            <Form.Group>
              <label className="nfe-dest-label">Número *</label>
              <Form.Control
                size="sm"
                type="number"
                {...register('numero', { required: true, min: 1 })}
                disabled={isReadOnly}
                min={1}
              />
            </Form.Group>
          </Col>
          <Col xs={12} md={6}>
            <Form.Group>
              <label className="nfe-dest-label">Natureza da Operação *</label>
              {isReadOnly ? (
                <Form.Control
                  size="sm"
                  type="text"
                  {...register('naturezaOperacao')}
                  disabled
                />
              ) : (
                <Controller
                  name="naturezaOperacao"
                  control={control}
                  rules={{ required: true }}
                  render={({ field }) => (
                    <NaturezaOperacaoInput
                      {...field}
                      size="sm"
                      placeholder="Ex: Venda de mercadoria"
                    />
                  )}
                />
              )}
            </Form.Group>
          </Col>
          <Col xs={12} md={4}>
            <Form.Group>
              <label className="nfe-dest-label">Data/Hora de Saída ou Entrada</label>
              <Form.Control
                size="sm"
                type="datetime-local"
                {...register('dhSaiEnt')}
                disabled={isReadOnly}
              />
            </Form.Group>
          </Col>
        </Row>
      </div>

      {/* ── Classificação ────────────────────────── */}
      <div className="nfe-dest-section">
        <div className="nfe-dest-section-header">
          <span className="nfe-dest-section-title">Classificação</span>
        </div>
        <Row className="g-3">
          <Col xs={12} sm={6} md={3}>
            <Form.Group>
              <label className="nfe-dest-label">Finalidade *</label>
              <Form.Select size="sm" {...register('finalidadeNFe')} disabled={isReadOnly}>
                <option value="1">1 — Normal</option>
                <option value="2">2 — Complementar</option>
                <option value="3">3 — Ajuste</option>
                <option value="4">4 — Devolução</option>
              </Form.Select>
            </Form.Group>
          </Col>
          <Col xs={12} sm={6} md={3}>
            <Form.Group>
              <label className="nfe-dest-label">Tipo de Nota *</label>
              <Form.Select size="sm" {...register('tipoNota')} disabled={isReadOnly}>
                <option value="0">0 — Entrada</option>
                <option value="1">1 — Saída</option>
              </Form.Select>
            </Form.Group>
          </Col>
          <Col xs={12} sm={6} md={3}>
            <label className="nfe-dest-label">Ambiente</label>
            <div className="small fw-medium">
              {nfe?.tipoAmbiente === '1' ? '1 — Produção' : '2 — Homologação'}
            </div>
          </Col>
        </Row>
      </div>

      {/* ── Indicadores ──────────────────────────── */}
      <div className="nfe-dest-section">
        <div className="nfe-dest-section-header">
          <span className="nfe-dest-section-title">Indicadores</span>
        </div>
        <Row className="g-3">
          <Col xs={12} sm={6} md={4}>
            <Form.Group>
              <label className="nfe-dest-label">Destino</label>
              <Form.Select size="sm" {...register('indicadorDestinatario')} disabled={isReadOnly}>
                <option value="">—</option>
                <option value="1">1 — Interno</option>
                <option value="2">2 — Interestadual</option>
                <option value="3">3 — Exterior</option>
              </Form.Select>
            </Form.Group>
          </Col>
          <Col xs={12} sm={6} md={4}>
            <Form.Group>
              <label className="nfe-dest-label">Consumidor Final</label>
              <Form.Select size="sm" {...register('indicadorFinal')} disabled={isReadOnly}>
                <option value="">—</option>
                <option value="0">0 — Normal</option>
                <option value="1">1 — Consumidor final</option>
              </Form.Select>
            </Form.Group>
          </Col>
          <Col xs={12} sm={6} md={4}>
            <Form.Group>
              <label className="nfe-dest-label">Presença do Comprador</label>
              <Form.Select size="sm" {...register('indicadorPresenca')} disabled={isReadOnly}>
                <option value="">—</option>
                <option value="0">0 — Não se aplica</option>
                <option value="1">1 — Presencial</option>
                <option value="2">2 — Internet</option>
                <option value="3">3 — Teleatendimento</option>
                <option value="4">4 — Entrega em domicílio</option>
                <option value="9">9 — Outros</option>
              </Form.Select>
            </Form.Group>
          </Col>
        </Row>
      </div>

      {!isReadOnly && (
        <div className="d-flex justify-content-end gap-2 mt-3 pt-3 border-top border-opacity-25">
          <Button
            type="submit"
            variant="primary"
            size="sm"
            disabled={!isDirty || mutation.isPending}
            loading={mutation.isPending}
            loadingPosition="start"
          >
            {mutation.isPending ? 'Salvando...' : 'Salvar Identificação'}
          </Button>
        </div>
      )}
    </Form>
  );
};

export default SecaoCabecalho;
